import React from 'react'
import styled from 'styled-components'
import Heading from 'Event/template/Cards/Dashboard/Sidebar/Heading'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import {Publishable} from 'Event/Dashboard/editor/views/Published'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import AddAgendaButton from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/AgendaList/AddAgendaButton'
import {useEditMode} from 'Event/EditModeProvider'
import {useAttendeeVariables} from 'Event'
import AgendaItem from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaItem'
import {DragDropContext, Droppable, DropResult} from 'react-beautiful-dnd'
import StyledText from 'lib/ui/typography/StyledText'
import {useCardsTemplate} from 'Event/template/Cards'
import {useToggle} from 'lib/toggle'
import {AgendaListConfig} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaListConfig'
import {FontStyle} from 'lib/ui/typography/FontStyleInput'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import Section from 'Event/template/Cards/Dashboard/Sidebar/Section'
import {createPositions, HashMap, Ordered, orderedIdsByPosition} from 'lib/list'
import {
  HasOffset,
  useEditSidebarItem,
} from 'Event/template/Cards/Dashboard/Sidebar/SidebarItem'
import {HasRules} from 'Event/attendee-rules'
import {HasSchedule} from 'lib/ui/layout/Scheduled'
import {useRemoveTranslations} from 'Event/LanguageProvider'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'

export const AGENDA_LIST = 'Agenda List'
export type AgendaListProps = Ordered &
  HasRules &
  HasOffset &
  HasSchedule & {
    type: typeof AGENDA_LIST
    title: string
    description?: string
    footer?: string
    descriptionFontStyles?: FontStyle[]
    footerFontStyles?: FontStyle[]
    items: HashMap<Agenda>
    isEnabled: boolean
  }

export const createAgendaList = (): AgendaListProps => ({
  type: AGENDA_LIST,
  title: 'Agenda',
  description: '',
  footer: 'Agenda Time is in YOUR time zone, not ours',
  items: {},
  footerFontStyles: [],
  descriptionFontStyles: [],
  isEnabled: true,
  offsetTop: 0,
  offsetBottom: 0,
})

export type Agenda = Publishable &
  HasRules &
  Ordered & {
    startDate: string
    endDate: string | null
    text: string
    link: string | null
  }

export default function AgendaList(props: AgendaListProps & {id: string}) {
  const {
    title,
    items,
    descriptionFontStyles,
    footer,
    description,
    footerFontStyles,
    isEnabled,
    offsetTop,
    offsetBottom,
  } = props
  const {sidebar} = useCardsTemplate()
  const isEdit = useEditMode()
  const v = useAttendeeVariables()
  const hasAgenda = Object.keys(items).length > 0
  const {flag: listConfigVisible, toggle: toggleListConfig} = useToggle()

  const showing = hasAgenda && isEnabled
  if (!showing && !isEdit) {
    return null
  }

  const HeadingHolder = (props: {children: React.ReactElement}) => {
    if (isEdit) {
      return <div style={{minHeight: 30}}>{props.children}</div>
    }

    return v(title) ? props.children : null
  }

  return (
    <Section offsetTop={offsetTop} offsetBottom={offsetBottom}>
      <EditModeOnly>
        <AgendaListConfig
          list={props}
          id={props.id}
          showing={listConfigVisible}
          onClose={toggleListConfig}
        />
        <RemoveAgendaListButton {...props} />
      </EditModeOnly>
      <Editable onEdit={toggleListConfig}>
        <HeadingHolder>
          <Heading aria-label="agendas">{v(title)}</Heading>
        </HeadingHolder>
      </Editable>
      <StyledText
        Component={Text}
        fontStyles={descriptionFontStyles}
        aria-label="agendas description"
        color={sidebar.textColor ?? ''}
      >
        {v(description || '')}
      </StyledText>
      <AgendaListContent {...props} />
      <StyledText
        Component={Text}
        fontStyles={footerFontStyles}
        aria-label="agendas footer description"
        color={sidebar.textColor ?? ''}
      >
        {v(footer || '')}
      </StyledText>
      <EditModeOnly>
        <StyledAddAgendaEventButton listId={props.id} />
      </EditModeOnly>
    </Section>
  )
}

function AgendaListContent(props: AgendaListProps & {id: string}) {
  const isEdit = useEditMode()

  if (!isEdit) {
    return <AgendaItemList {...props} />
  }

  return <DroppableList {...props} />
}

function DroppableList(props: AgendaListProps & {id: string}) {
  const handleDrag = useHandleDrag(props)

  return (
    <DragDropContext onDragEnd={handleDrag}>
      <Droppable droppableId="drag-and-drop-agendas">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            <>
              <AgendaItemList {...props} />
              {provided.placeholder}
            </>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

function RemoveAgendaListButton(props: AgendaListProps & {id: string}) {
  const {items, id} = props
  const saveTemplate = useSaveTemplate()
  const removeTranslation = useRemoveTranslations()

  const remove = () => {
    const agendaKeys = Object.keys(items)
    saveTemplate({
      sidebarItems: {[id]: REMOVE},
      localization: removeTranslation([
        `${id}_title`,
        `${id}_description`,
        ...agendaKeys.flatMap((key) => [`${key}_text`, `${key}_link`]),
      ]),
    })
    removeTranslation(agendaKeys)
  }

  return (
    <RemoveButton size="large" showing onClick={remove}>
      Remove Agenda
    </RemoveButton>
  )
}

function AgendaItemList(props: AgendaListProps & {id: string}) {
  const {items} = props
  const ids = orderedIdsByPosition(items)

  return (
    <>
      {ids.map((id, index: number) => (
        <AgendaItem
          id={id}
          key={id}
          agenda={items[id]}
          index={index}
          list={props}
          listId={props.id}
        />
      ))}
    </>
  )
}

function useHandleDrag(props: AgendaListProps) {
  const {items} = props
  const {update} = useEditSidebarItem()

  return (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }

    const ids = orderedIdsByPosition(items)
    const [removed] = ids.splice(source.index, 1)
    ids.splice(destination.index, 0, removed)

    update({
      items: createPositions(ids),
    })
  }
}

const StyledAddAgendaEventButton = styled(AddAgendaButton)`
  margin-bottom: ${(props) => props.theme.spacing[6]}!important;
`

const Text = styled.p<{
  color: string
}>`
  color: ${(props) => props.color};
`
